import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Box, Typography, CircularProgress
} from '@mui/material';
import AuthContext from '../../contexts/AuthContext';

/**
 * Wizard dialog for inviting a user to an organization.
 * Steps:
 * 1) Email lookup
 * 2) Role selection (may finish here)
 * 3) NPI entry for new Provider
 * 4) Provider details review/edit
 */
export default function InviteUserDialog({ open, onClose, organizationId, onInvited }) {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [wizardStep, setWizardStep] = useState(1);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [role, setRole] = useState('');
  const [roleError, setRoleError] = useState('');
  const [orgUserEmails, setOrgUserEmails] = useState([]);
  const [hasProfessionRecord, setHasProfessionRecord] = useState(false);
  // Provider invite state
  const [providerNpi, setProviderNpi] = useState('');
  const [npiData, setNpiData] = useState(null);
  const [npiError, setNpiError] = useState('');
  const [npiLoading, setNpiLoading] = useState(false);
  const [providerDetails, setProviderDetails] = useState({
    name_prefix: '', first_name: '', middle_name: '', last_name: '', name_suffix: '',
    credential: '', provider_phone: '', provider_fax: ''
  });
  const [providerDetailsErrors, setProviderDetailsErrors] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  // Role options passed implicitly (phase2 flag from parent context)
  const roleOptions = ['Provider', 'OfficeStaff'];
  
  // Derived validity flags
  const isEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  // Reset dialog state on open
  useEffect(() => {
    if (open) {
      setWizardStep(1);
      setEmail(''); setEmailError(''); setEmailLoading(false);
      setRole(''); setRoleError(''); setHasProfessionRecord(false);
      setProviderNpi(''); setNpiData(null);
      setNpiError(''); setNpiLoading(false);
      setProviderDetails({ name_prefix: '', first_name: '', middle_name: '', last_name: '', name_suffix: '', credential: '', provider_phone: '', provider_fax: '' });
      setProviderDetailsErrors({}); setInviteLoading(false); setInviteError('');
    }
  }, [open]);

  // Load existing org user emails for duplicate check
  useEffect(() => {
    if (!open || !tokens?.id_token || !organizationId) return;
    fetch(`${apiBase}/organizations/${organizationId}/users?skip=0&limit=1000`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setOrgUserEmails((data.items||[]).map(u => u.email.toLowerCase())))
      .catch(() => setOrgUserEmails([]));
  }, [open, tokens, organizationId, apiBase]);

  const handleEmailNext = () => {
    const addr = email.trim().toLowerCase();
    if (!addr) { setEmailError('Email is required'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) { setEmailError('Invalid email address'); return; }
    setEmailError(''); setEmailLoading(true);
    fetch(`${apiBase}/users?search_text=${encodeURIComponent(addr)}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const match = (data.items||[]).find(u => u.email.toLowerCase()===addr);
        setHasProfessionRecord(!!(match?.professional_record));
      })
      .catch(() => setHasProfessionRecord(false))
      .finally(() => { setEmailLoading(false); setWizardStep(2); });
  };

  const handleRoleNext = () => {
    if (!role) { setRoleError('Role is required'); return; }
    setRoleError('');
    if (role==='Provider' && !hasProfessionRecord) {
      setWizardStep(3);
    } else {
      sendInvite();
    }
  };

  // Step 3 Next: fetch NPI data and advance on success
  const handleNpiNext = async () => {
    const npi = providerNpi.trim();
    if (!npi) {
      setNpiError('NPI is required');
      return;
    }
    setNpiError('');
    setNpiLoading(true);
    try {
      const res = await fetch(`${apiBase}/npi?number=${npi}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.Errors?.[0]?.description || 'Failed to fetch NPI');
      }
      if (data.Errors) {
        throw new Error(data.Errors[0].description);
      }
      if (!data.results || data.results.length === 0) {
        throw new Error('No NPI found with that number');
      }
      const first = data.results[0];
      if (first.enumeration_type !== 'NPI-1') {
        throw new Error('Please enter a personal NPI number (NPI-1)');
      }
      setNpiData(first);
      // Prefill provider details from NPI
      setProviderDetails({
        name_prefix: first.basic.name_prefix || '',
        first_name: first.basic.first_name || '',
        middle_name: first.basic.middle_name || '',
        last_name: first.basic.last_name || '',
        name_suffix: first.basic.name_suffix || '',
        credential: first.basic.credential || '',
        provider_phone: first.addresses?.[0]?.telephone_number || '',
        provider_fax: first.addresses?.[0]?.fax_number || ''
      });
      setProviderDetailsErrors({});
      setWizardStep(4);
    } catch (err) {
      setNpiError(err.message || 'Failed to fetch NPI');
    } finally {
      setNpiLoading(false);
    }
  };

  // Deprecated: fetching moved into handleNpiNext

  const handleBack = () => {
    setInviteError(''); setRoleError(''); setEmailError(''); setNpiError('');
    setWizardStep(prev => Math.max(1, prev-1));
  };

  const sendInvite = () => {
    // common validations
    if (orgUserEmails.includes(email.trim().toLowerCase())) {
      setInviteError('User already has a role'); return;
    }
    if (role==='Provider') {
      // ensure providerDetails valid
      const errs = {};
      if (!providerDetails.first_name) errs.first_name='First name is required';
      if (!providerDetails.last_name) errs.last_name='Last name is required';
      if (!providerDetails.provider_phone) errs.provider_phone='Phone is required';
      if (!providerDetails.provider_fax) errs.provider_fax='Fax is required';
      setProviderDetailsErrors(errs);
      if (Object.keys(errs).length) { setInviteError('Fix errors'); return; }
    }
    setInviteError(''); setInviteLoading(true);
    const payload = { email, organization_id: organizationId, role };
    if (role === 'Provider') {
      // Attach professional info for provider invitations
      payload.professional_info = {
        npi: providerNpi.trim(),
        first_name: providerDetails.first_name,
        middle_name: providerDetails.middle_name,
        last_name: providerDetails.last_name,
        name_prefix: providerDetails.name_prefix,
        name_suffix: providerDetails.name_suffix,
      };
    }
    fetch(`${apiBase}/invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.id_token}`
      },
      body: JSON.stringify(payload)
    })
      .then(res => res.ok
        ? res.json()
        : res.json().then(d => Promise.reject(d.detail || 'Invite failed'))
      )
      .then(() => {
        onInvited();
        onClose();  // dismiss the dialog after successful invite
      })
      .catch(err => setInviteError(
        typeof err === 'string' ? err : err.detail || JSON.stringify(err)
      ))
      .finally(() => setInviteLoading(false));
  };

  const steps = [1,2,3,4];
  // Descriptions per step
  const stepDescription = {
    1: 'Enter the email address of the user you want to invite.',
    2: 'Select the role for the user.',
    3: 'Enter the user\'s NPI (National Provider Identifier).',
    4: 'Review and update the provider details to complete the invite.',
  }[wizardStep];

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        {/* Show previous steps summary */}
        {wizardStep > 1 && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>Email:</Typography>
              <Typography variant="body2" component="span">{email}</Typography>
            </Box>
            {wizardStep > 2 && (
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>Role:</Typography>
                <Typography variant="body2" component="span">{role}</Typography>
              </Box>
            )}
            {wizardStep > 3 && (
              <Box display="flex" alignItems="center">
                <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>NPI:</Typography>
                <Typography variant="body2" component="span">{providerNpi}</Typography>
              </Box>
            )}
          </Box>
        )}
        {/* Step description */}
        <Typography variant="body2" gutterBottom>{stepDescription}</Typography>
        {wizardStep === 1 && (
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            disabled={emailLoading}
          />
        )}
        {wizardStep===2 && (
          <FormControl fullWidth margin="dense" error={!!roleError}>
            <InputLabel>Role</InputLabel>
            <Select value={role} onChange={e=>setRole(e.target.value)} label="Role">
              {roleOptions.map(r=><MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
            {roleError && <Typography color="error" variant="body2">{roleError}</Typography>}
          </FormControl>
        )}
        {wizardStep===3 && (
          <TextField
            autoFocus
            margin="dense"
            label="Provider NPI"
            fullWidth
            variant="standard"
            value={providerNpi}
            onChange={e=>setProviderNpi(e.target.value)}
            error={!!npiError}
            helperText={npiError}
            disabled={npiLoading}
          />
        )}
        {wizardStep===4 && npiData && (
          <Box component="form" noValidate>
            <Typography variant="subtitle1" gutterBottom>Provider details</Typography>
            {['name_prefix','first_name','middle_name','last_name','name_suffix','credential','provider_phone','provider_fax'].map(field=>{
              const base = field.replace(/_/g,' ').trim();
              const label = base ? base.replace(/\b\w/g, c => c.toUpperCase()) : '';
              const err = providerDetailsErrors[field];
              return (
                <TextField key={field} fullWidth margin="dense" label={label}
                  variant="standard" value={providerDetails[field]||''}
                  onChange={e=>setProviderDetails(prev=>({...prev,[field]:e.target.value}))}
                  error={!!err} helperText={err}
                  required={['first_name','last_name','provider_phone','provider_fax'].includes(field)}
                />
              );
            })}
          </Box>
        )}
        {inviteError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {inviteError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {inviteLoading ? (
          <>
            <span />
            <Box>
              <CircularProgress size={20} />
            </Box>
          </>
        ) : inviteError ? (
          <>
            <span />
            <Box>
              <Button onClick={onClose} variant="contained" color="primary">Close</Button>
            </Box>
          </>
        ) : (
          <>
            {/* Cancel on the left when idle */}
            <Button onClick={onClose} disabled={emailLoading||npiLoading} variant="text" color="primary">
              Cancel
            </Button>
            {/* Other actions on the right */}
            <Box>
              {wizardStep>1 && (
                <Button onClick={handleBack} disabled={emailLoading||npiLoading} variant="text" color="primary" sx={{ mr: 1 }}>
                  Back
                </Button>
              )}
              {wizardStep===1 && (
                <Button onClick={handleEmailNext} disabled={!isEmailValid || emailLoading} variant="contained" color="primary">
                  Next
                </Button>
              )}
              {wizardStep===2 && (
                <Button onClick={handleRoleNext} disabled={!role} variant="contained" color="primary">
                  {hasProfessionRecord||role!=='Provider'?'Send Invite':'Next'}
                </Button>
              )}
              {wizardStep===3 && (
                <Button onClick={handleNpiNext} disabled={npiLoading} variant="contained" color="primary">
                  Next
                </Button>
              )}
              {wizardStep===4 && (
                <Button
                  onClick={sendInvite}
                  disabled={
                    !providerDetails.first_name ||
                    !providerDetails.last_name ||
                    !providerDetails.provider_phone ||
                    !providerDetails.provider_fax
                  }
                  variant="contained"
                  color="primary"
                >
                  Send Invite
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
